import { contactStrings } from '@data/contacts';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export type ContactFormItemAction = 'create' | 'edit';

export type ContactFormItem =
  | {
      /**
       * For creating client objects, not uploaded to server yet
       */
      action: 'create';
      id: number;
      mode: 'edit' | 'sameAsOwner';
    }
  | {
      /**
       * For editing server objects
       */
      action: 'edit';
      id: number;
      initialOutput: BaseContactForm;
      mode: 'edit' | 'display' | 'sameAsOwner';
      displayOnly: boolean;
    };

/**
 * + createSuccess: create a banner letting the user know creating is successful
 * + editSuccess: create a banner when user successfully edit contacts or owned contacts
 * + editWithScrollBottom: scroll to the bottom automatically.
 * + createOwnedContact: add an empty `action: create` `ContactFormItem` at the beginning, then scroll to it.
 */
export const ContactDisplayAction = z.enum([
  'createSuccess',
  'editSuccess',
  'editWithScrollBottom',
  'createOwnedContact',
]);

export type ContactDisplayAction = z.infer<typeof ContactDisplayAction>;

export const ContactType = z.enum(['consignee', 'pickup_address', 'receiver', 'shipper']);

export type ContactType = z.infer<typeof ContactType>;

export const ContactFields = z.object({
  id: z.number(),
  company_name: z.string().label(contactStrings.company_name),
  english_name: z.string().label(contactStrings.english_name),
  zip_code: z.string().label(contactStrings.zip_code),
  address1: z.string().label(contactStrings.address1),
  address1_cn: z.string().label(contactStrings.address1_cn),
  address2: z.string().label(contactStrings.address2),
  principal_name: z.string().label(contactStrings.principal_name),
  phone: z.string().label(contactStrings.phone),
  email: z.string().label(contactStrings.email),
  sns_id: z.string().label(contactStrings.sns_id),
  social_credit_id: z.string().label(contactStrings.social_credit_id),

  owner: z.number().nullish(),
  user: z.number().nullish(),
  contact_type: ContactType,
  company_id: z.number().nullish(),

  use_count: z.number(),
  related_contacts: z.number().array(),

  created_at: z.string(),
  updated_at: z.string(),
});

export const BookingInquiryContact = z.object({
  id: ContactFields.shape.id.nullish(),
  company_name: ContactFields.shape.company_name,
  english_name: ContactFields.shape.english_name,
  zip_code: ContactFields.shape.zip_code,
  address1: ContactFields.shape.address1,
  address1_cn: ContactFields.shape.address1_cn,
  address2: ContactFields.shape.address2,
  principal_name: ContactFields.shape.principal_name,
  phone: ContactFields.shape.phone,
  email: ContactFields.shape.email,
  sns_id: ContactFields.shape.sns_id,
  owner: ContactFields.shape.owner,
  user: ContactFields.shape.user,
  social_credit_id: ContactFields.shape.social_credit_id,
});

export type BookingInquiryContact = z.infer<typeof BookingInquiryContact>;

export const Contact = z.object({
  id: ContactFields.shape.id,
  company_name: ContactFields.shape.company_name,
  english_name: ContactFields.shape.english_name,
  zip_code: ContactFields.shape.zip_code,
  address1: ContactFields.shape.address1,
  address1_cn: ContactFields.shape.address1_cn,
  address2: ContactFields.shape.address2,
  principal_name: ContactFields.shape.principal_name,
  phone: ContactFields.shape.phone,
  email: ContactFields.shape.email,
  sns_id: ContactFields.shape.sns_id,
  social_credit_id: ContactFields.shape.social_credit_id,

  owner: ContactFields.shape.owner,
  user: ContactFields.shape.user,
  contact_type: ContactFields.shape.contact_type,
  company_id: ContactFields.shape.company_id,

  use_count: ContactFields.shape.use_count,
  related_contacts: ContactFields.shape.related_contacts,

  created_at: ContactFields.shape.created_at,
  updated_at: ContactFields.shape.updated_at,
});

export type Contact = z.infer<typeof Contact>;

export const ContactSearchQuery = z
  .object({
    contact_type: ContactFields.shape.contact_type,
    ordering: z.string().array(),
    company_name: ContactFields.shape.company_name,
    principal_name: ContactFields.shape.principal_name,
    related_to: z.number(),
    sort_by_related_to: z.number(),
    owner_id: z.number(),
    creator_id: z.number(),
    is_user: z.boolean(),
  })
  .partial();

export type ContactSearchQuery = z.infer<typeof ContactSearchQuery>;

export const PostContact = z.object({
  company_name: ContactFields.shape.company_name,
  english_name: ContactFields.shape.english_name,
  zip_code: ContactFields.shape.zip_code,
  address1: ContactFields.shape.address1,
  address1_cn: ContactFields.shape.address1_cn,
  address2: ContactFields.shape.address2,
  principal_name: ContactFields.shape.principal_name,
  phone: ContactFields.shape.phone,
  email: ContactFields.shape.email,
  sns_id: ContactFields.shape.sns_id,
  contact_type: ContactFields.shape.contact_type,
  owner: ContactFields.shape.owner,
  social_credit_id: ContactFields.shape.social_credit_id,
});

export type PostContact = z.infer<typeof PostContact>;

export const PatchContact = z
  .object({
    company_name: ContactFields.shape.company_name,
    english_name: ContactFields.shape.english_name,
    zip_code: ContactFields.shape.zip_code,
    address1: ContactFields.shape.address1,
    address1_cn: ContactFields.shape.address1_cn,
    address2: ContactFields.shape.address2,
    principal_name: ContactFields.shape.principal_name,
    phone: ContactFields.shape.phone,
    email: ContactFields.shape.email,
    sns_id: ContactFields.shape.sns_id,
    contact_type: ContactFields.shape.contact_type,
    social_credit_id: ContactFields.shape.social_credit_id,

    related_contacts: ContactFields.shape.related_contacts,
  })
  .partial();

export type PatchContact = z.infer<typeof PatchContact>;

export const BaseContactForm = z.object({
  /**
   * Required for JP, optional for CN
   */
  company_name: ContactFields.shape.company_name,
  english_name: f.withEnglishRegex(ContactFields.shape.english_name.min(1)),
  zip_code: ContactFields.shape.zip_code.min(1),
  /**
   * Must be English for CN
   */
  address1: ContactFields.shape.address1.min(1),
  address1_cn: ContactFields.shape.address1_cn,
  address2: ContactFields.shape.address2,
  principal_name: ContactFields.shape.principal_name.min(1),
  phone: f.withPhoneRegex(ContactFields.shape.phone),
  email: ContactFields.shape.email.email().optional(),
  sns_id: ContactFields.shape.sns_id,
  social_credit_id: ContactFields.shape.social_credit_id
    .regex(/^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/, contactStrings.usciFormatError)
    .optional(),
  user: ContactFields.shape.user,
});

export type BaseContactForm = z.infer<typeof BaseContactForm>;

export const ContactFormCN = z.object({
  ...BaseContactForm.shape,
  address1: f.withEnglishRegex(ContactFields.shape.address1).label(contactStrings.city),
  address2: ContactFields.shape.address2.label(contactStrings.address),
});

export type ContactFormCN = z.infer<typeof ContactFormCN>;

export const ContactFormJP = z.object({
  ...BaseContactForm.shape,
  zip_code: f.withJapaneseZipcodeRegex(BaseContactForm.shape.zip_code),
});

export type ContactFormJP = z.infer<typeof ContactFormJP>;

export const BaseContactAddForm = z.object({
  contact: BaseContactForm,
  /**
   * Must not be empty to JP, can be empty for CN
   */
  ownedContacts: BaseContactForm.array(),
  sameAsOwnerIds: z.number().array(),
});

export type BaseContactAddForm = z.infer<typeof BaseContactAddForm>;

export const ContactAddFormCN = z.object({
  ...BaseContactAddForm.shape,
  contact: ContactFormCN,
  ownedContacts: ContactFormCN.array(),
});

export type ContactAddFormCN = z.infer<typeof ContactAddFormCN>;

export const ContactAddFormJP = z.object({
  ...BaseContactAddForm.shape,
  contact: ContactFormJP,
  ownedContacts: ContactFormJP.array().nonempty(contactStrings.receiverRequired),
});

export type ContactAddFormJP = z.infer<typeof ContactAddFormJP>;

export class ContactHelper {
  static mapToForm(contact: Contact): BaseContactForm {
    return contact;
  }

  static areEqual(a: Contact, b: Contact) {
    const keys: (keyof Contact)[] = [
      'company_name',
      'english_name',
      'zip_code',
      'address1',
      'address2',
      'principal_name',
      'phone',
      'email',
      'sns_id',
    ];

    return keys.every((k) => a[k] === b[k]);
  }
}

export const jpContacts: ContactType[] = ['receiver', 'consignee'];

export const exampleContactAddFormJP: ContactAddFormJP = {
  contact: {
    company_name: '株式会社中国人',
    english_name: 'Chinaman Company',
    zip_code: '1100016',
    address1: 'Taito 1-15-6 903 Aroma Court',
    address1_cn: '',
    address2: '',
    principal_name: '麻烦制造者',
    phone: '+819063974539',
    sns_id: '',
  },
  ownedContacts: [
    {
      company_name: '株式会社中国人',
      english_name: 'Chinaman Company',
      zip_code: '1100016',
      address1: 'Taito 1-15-6 903 Aroma Court',
      address1_cn: '',
      address2: '',
      principal_name: '麻烦制造者',
      phone: '+819063974539',
      sns_id: '',
    },
  ],
  sameAsOwnerIds: [],
};

export const exampleContactAddFormCN: ContactAddFormCN = {
  contact: {
    company_name: '芝麻仁公司',
    english_name: 'Seasame Limited',
    zip_code: '200233',
    address1: 'Shanghai, Xuhui',
    address1_cn: '',
    address2: '',
    principal_name: '周杰伦',
    phone: '13902913666',
    social_credit_id: '',
    sns_id: '',
  },
  ownedContacts: [
    {
      company_name: '芝麻仁公司',
      english_name: 'Seasame Limited',
      zip_code: '200233',
      address1: 'Shanghai, Xuhui',
      address1_cn: '',
      address2: '',
      principal_name: '周杰伦',
      phone: '13902913666',
      sns_id: '',
    },
  ],
  sameAsOwnerIds: [],
};
